/**
 * 弃牌弹窗 - 手牌超出上限时弃牌
 * 
 * 当换牌后手牌超过 MAX_HAND_SIZE 时，玩家需要弃掉多余的卡牌
 */

import React from 'react';
import { CardItem } from '../cards/CardItem';
import type { GameCard } from '../../../types';
import { MAX_HAND_SIZE } from '../../../constants';

interface DiscardCardsModalProps {
  /** 当前手牌 (玩家从中选择要弃掉的) */
  handCards: GameCard[];
  /** 需要弃掉的数量 */
  needToDiscard: number;
  selectedIds: Set<string>;
  selectedCount: number;
  canConfirm: boolean;
  onToggleCard: (card: GameCard) => void;
  onConfirm: () => void;
}

export const DiscardCardsModal: React.FC<DiscardCardsModalProps> = ({
  handCards,
  needToDiscard,
  selectedIds,
  selectedCount,
  canConfirm,
  onToggleCard,
  onConfirm,
}) => {
  if (needToDiscard <= 0) return null;

  return (
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-50 via-white to-red-50 p-5 border-b border-red-200 shrink-0">
          <h3 className="text-xl font-bold text-red-700 text-center">
            手牌已满 - 需要弃牌
          </h3>
          <div className="flex justify-center items-center gap-4 mt-3">
            <StatBadge label="当前手牌" value={handCards.length} />
            <StatBadge label="手牌上限" value={MAX_HAND_SIZE} />
          </div>
          <p className="text-sm text-red-600 text-center mt-3 font-medium">
            请选择 <span className="text-lg font-bold text-red-700">{needToDiscard}</span> 张卡牌弃掉
          </p>
        </div>

        {/* Cards Grid */}
        <div className="flex-1 overflow-y-auto p-4 bg-[#FAFAFA]">
          <div className="mb-2">
            <h4 className="text-sm font-bold text-[#6B7280] mb-3 flex items-center gap-2">
              <span>你的手牌</span>
              <span className="text-xs font-normal text-[#9CA3AF]">（点击选择要弃掉的牌）</span>
            </h4>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 max-w-3xl mx-auto">
              {handCards.map((card) => (
                <CardItem
                  key={card.id}
                  card={card}
                  isSelected={selectedIds.has(card.id)}
                  selectionMode="discard"
                  onClick={() => onToggleCard(card)}
                  showDescription
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gradient-to-r from-red-50 via-white to-red-50 p-4 border-t border-red-200 shrink-0">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            <div className="text-sm">
              <span className="text-[#6B7280]">已选择弃牌: </span>
              <span className={`font-bold ${selectedCount === needToDiscard ? 'text-green-600' : 'text-red-600'}`}>
                {selectedCount}/{needToDiscard}
              </span>
              {selectedCount !== needToDiscard && (
                <span className="text-red-500 text-xs ml-2">
                  还需要选择 {needToDiscard - selectedCount} 张
                </span>
              )}
            </div>
            <button
              onClick={onConfirm}
              disabled={!canConfirm}
              className={`
                px-8 py-3 rounded-xl font-bold text-white transition-all
                ${canConfirm
                  ? 'bg-gradient-to-r from-red-500 to-red-600 shadow-lg hover:shadow-xl hover:-translate-y-0.5'
                  : 'bg-[#E5E0D8] cursor-not-allowed'
                }
              `}
            >
              确认弃牌
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 统计徽章组件
 */
interface StatBadgeProps {
  label: string;
  value: number;
}

const StatBadge: React.FC<StatBadgeProps> = ({ label, value }) => (
  <div className="px-3 py-1.5 rounded-lg border bg-[#F5EDE3] text-[#6B7280] border-[#E5E0D8]">
    <span className="text-xs">{label}: </span>
    <span className="font-bold">{value}</span>
  </div>
);
